import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Page, 
  Layout, 
  Card,
  Text,
  BlockStack,
  InlineStack,
  Select,
  Box,
  Banner,
  Spinner,
  ProgressBar,
  DataTable,
  Badge,
  Grid,
  Divider
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { AnalyticsCard } from '../components/AnalyticsCard';
import { useAuthenticatedFetch } from '../hooks/useApi';

interface AnalyticsData {
  totalViews: number;
  totalClicks: number;
  totalConversions: number;
  totalRevenue: number;
  clickThroughRate: number;
  conversionRate: number;
  averageOrderValue: number;
  topProducts: Array<{
    productId: string;
    productTitle: string;
    views: number;
    clicks: number;
    conversions: number;
  }>;
  behaviorBreakdown: {
    productViews: number;
    addToCarts: number;
    purchases: number;
    searches: number;
  };
  dailyMetrics: Array<{
    date: string;
    views: number;
    clicks: number;
    conversions: number;
    revenue: number;
  }>;
  recommendationPerformance: {
    collaborative: { impressions: number; clicks: number; conversions: number };
    contentBased: { impressions: number; clicks: number; conversions: number };
    trending: { impressions: number; clicks: number; conversions: number };
  };
}

const defaultAnalytics: AnalyticsData = {
  totalViews: 0,
  totalClicks: 0,
  totalConversions: 0,
  totalRevenue: 0,
  clickThroughRate: 0,
  conversionRate: 0,
  averageOrderValue: 0,
  topProducts: [],
  behaviorBreakdown: {
    productViews: 0,
    addToCarts: 0,
    purchases: 0,
    searches: 0,
  },
  dailyMetrics: [],
  recommendationPerformance: {
    collaborative: { impressions: 0, clicks: 0, conversions: 0 },
    contentBased: { impressions: 0, clicks: 0, conversions: 0 },
    trending: { impressions: 0, clicks: 0, conversions: 0 },
  },
};

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();

  const [analytics, setAnalytics] = useState<AnalyticsData>(defaultAnalytics);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    async function fetchAnalytics() {
      if (!shop) return;

      setIsLoading(true);
      setError(null);

      try {
        // Parse time range to days
        const daysMap: { [key: string]: number } = { '7d': 7, '30d': 30, '90d': 90 };
        const days = daysMap[timeRange] || 7;
        
        // Fetch behavior analytics AND attribution data in parallel
        const [response, attributionResponse] = await Promise.all([
          fetchWithAuth(`/api/behavior/analytics?days=${days}`),
          fetchWithAuth(`/api/attribution/overview?days=${days}`).catch(() => null)
        ]);
        
        // Map backend response to frontend structure
        const backendData = response as {
          totalProductViews: number;
          totalAddToCarts: number;
          totalPurchases: number;
          recommendationClicks: number;
          conversionRate: number;
          topProducts?: Array<{
            productId: number;
            productHandle?: string;
            views: number;
            addToCarts: number;
            purchases: number;
          }>;
          dailyStats?: Array<{
            date: string;
            views: number;
            addToCarts: number;
            purchases: number;
          }>;
        };

        // Attribution data from Phase 1 tracking system
        const attrData = attributionResponse as {
          totalClicks?: number;
          totalConversions?: number;
          totalRevenue?: number;
          conversionRate?: number;
          aov?: number;
          avgTimeToConversion?: number;
          metricsByType?: Record<string, {
            clicks: number;
            conversions: number;
            revenue: number;
            avgPosition: number;
            conversionRate: number;
          }>;
          metricsByAlgorithm?: Record<string, {
            clicks: number;
            conversions: number;
            revenue: number;
          }>;
        } | null;

        // Use attribution data for revenue/AOV/conversion metrics when available
        const attrClicks = attrData?.totalClicks ?? 0;
        const attrConversions = attrData?.totalConversions ?? 0;
        const attrRevenue = attrData?.totalRevenue ?? 0;
        const attrAOV = attrData?.aov ?? 0;
        const attrCVR = attrData?.conversionRate ?? 0;

        // Map metricsByType to recommendation performance
        const typeMetrics = attrData?.metricsByType ?? {};
        const getTypeData = (type: string) => {
          const m = typeMetrics[type];
          return m ? { impressions: m.clicks, clicks: m.clicks, conversions: m.conversions } 
                   : { impressions: 0, clicks: 0, conversions: 0 };
        };

        const transformedData: AnalyticsData = {
          totalViews: backendData.totalProductViews || 0,
          totalClicks: attrClicks || backendData.recommendationClicks || 0,
          totalConversions: attrConversions || backendData.totalPurchases || 0,
          totalRevenue: attrRevenue,
          clickThroughRate: backendData.totalProductViews > 0 
            ? ((attrClicks || backendData.recommendationClicks) / backendData.totalProductViews) * 100 
            : 0,
          conversionRate: attrCVR || backendData.conversionRate || 0,
          averageOrderValue: attrAOV,
          topProducts: (backendData.topProducts || []).map(p => ({
            productId: String(p.productId),
            productTitle: p.productHandle || `Product ${p.productId}`,
            views: p.views,
            clicks: p.addToCarts, // Using addToCarts as clicks proxy
            conversions: p.purchases,
          })),
          behaviorBreakdown: {
            productViews: backendData.totalProductViews || 0,
            addToCarts: backendData.totalAddToCarts || 0,
            purchases: backendData.totalPurchases || 0,
            searches: 0,
          },
          dailyMetrics: (backendData.dailyStats || []).map(d => ({
            date: d.date,
            views: d.views,
            clicks: d.addToCarts,
            conversions: d.purchases,
            revenue: 0,
          })),
          recommendationPerformance: {
            collaborative: getTypeData('collaborative'),
            contentBased: getTypeData('content_based'),
            trending: getTypeData('trending'),
          },
        };

        setAnalytics(transformedData);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError(t('analytics.loadError'));
        // Keep default empty analytics instead of demo data
        setAnalytics(defaultAnalytics);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAnalytics();
  }, [shop, timeRange, fetchWithAuth]);

  if (!shop) {
    return (
      <Page>
        <Banner tone="warning" title={t('dashboard.errors.shopNotFound')}>
          <p>{t('dashboard.errors.shopNotFoundDesc')}</p>
        </Banner>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page>
        <Box padding="800">
          <BlockStack gap="400" align="center">
            <Spinner size="large" />
            <Text as="p">{t('common.loading')}</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  const topProductsRows = analytics.topProducts.map(product => [
    product.productTitle,
    product.views.toLocaleString(),
    product.clicks.toLocaleString(),
    product.conversions.toLocaleString(),
    ((product.clicks / product.views) * 100).toFixed(1) + '%',
  ]);

  const totalBehaviors = 
    analytics.behaviorBreakdown.productViews + 
    analytics.behaviorBreakdown.addToCarts + 
    analytics.behaviorBreakdown.purchases + 
    analytics.behaviorBreakdown.searches;

  const getPercentage = (value: number) => totalBehaviors > 0 ? (value / totalBehaviors) * 100 : 0;

  return (
    <Page
      title={t('analytics.title')}
      backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="warning" title={t('analytics.usingDemoData')}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <InlineStack align="end">
            <Box width="200px">
              <Select
                label={t('analytics.timeRange')}
                labelHidden
                options={[
                  { label: t('analytics.last7Days'), value: '7d' },
                  { label: t('analytics.last30Days'), value: '30d' },
                  { label: t('analytics.last90Days'), value: '90d' },
                  { label: t('analytics.allTime'), value: 'all' },
                ]}
                value={timeRange}
                onChange={setTimeRange}
              />
            </Box>
          </InlineStack>
        </Layout.Section>

        <Layout.Section>
          <Grid>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <AnalyticsCard
                title={t('analytics.totalViews')}
                value={analytics.totalViews.toLocaleString()}
                change="+12%"
                trend="up"
              />
            </Grid.Cell>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <AnalyticsCard
                title={t('analytics.totalClicks')}
                value={analytics.totalClicks.toLocaleString()}
                change="+8%"
                trend="up"
              />
            </Grid.Cell>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <AnalyticsCard
                title={t('analytics.totalConversions')}
                value={analytics.totalConversions.toLocaleString()}
                change="+15%"
                trend="up"
              />
            </Grid.Cell>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <AnalyticsCard
                title={t('analytics.totalRevenue')}
                value={`$${analytics.totalRevenue.toLocaleString()}`}
                change="+22%"
                trend="up"
              />
            </Grid.Cell>
          </Grid>
        </Layout.Section>

        <Layout.Section>
          <Grid>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 4, lg: 4, xl: 4 }}>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h3">{t('analytics.clickThroughRate')}</Text>
                  <Text variant="heading2xl" as="p">{analytics.clickThroughRate.toFixed(1)}%</Text>
                  <ProgressBar progress={analytics.clickThroughRate} tone="primary" size="small" />
                </BlockStack>
              </Card>
            </Grid.Cell>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 4, lg: 4, xl: 4 }}>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h3">{t('analytics.conversionRate')}</Text>
                  <Text variant="heading2xl" as="p">{analytics.conversionRate.toFixed(1)}%</Text>
                  <ProgressBar progress={analytics.conversionRate} tone="success" size="small" />
                </BlockStack>
              </Card>
            </Grid.Cell>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 4, lg: 4, xl: 4 }}>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h3">{t('analytics.averageOrderValue')}</Text>
                  <Text variant="heading2xl" as="p">${analytics.averageOrderValue.toFixed(2)}</Text>
                  <Text variant="bodySm" as="p" tone="subdued">{t('analytics.fromRecommendationClicks')}</Text>
                </BlockStack>
              </Card>
            </Grid.Cell>
          </Grid>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h3">{t('analytics.topPerformingProducts')}</Text>
              <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
                headings={[t('analytics.productTitle'), t('analytics.views'), t('analytics.clicks'), t('analytics.conversions'), t('analytics.ctr')]}
                rows={topProductsRows}
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h3">{t('analytics.customerBehaviorBreakdown')}</Text>
              <Divider />
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text as="span">{t('analytics.productViews')}</Text>
                  <Badge>{analytics.behaviorBreakdown.productViews.toLocaleString()}</Badge>
                </InlineStack>
                <ProgressBar progress={getPercentage(analytics.behaviorBreakdown.productViews)} tone="primary" />
                
                <InlineStack align="space-between">
                  <Text as="span">{t('analytics.addToCarts')}</Text>
                  <Badge>{analytics.behaviorBreakdown.addToCarts.toLocaleString()}</Badge>
                </InlineStack>
                <ProgressBar progress={getPercentage(analytics.behaviorBreakdown.addToCarts)} tone="highlight" />
                
                <InlineStack align="space-between">
                  <Text as="span">{t('analytics.purchases')}</Text>
                  <Badge tone="success">{analytics.behaviorBreakdown.purchases.toLocaleString()}</Badge>
                </InlineStack>
                <ProgressBar progress={getPercentage(analytics.behaviorBreakdown.purchases)} tone="success" />
                
                <InlineStack align="space-between">
                  <Text as="span">{t('analytics.searches')}</Text>
                  <Badge>{analytics.behaviorBreakdown.searches.toLocaleString()}</Badge>
                </InlineStack>
                <ProgressBar progress={getPercentage(analytics.behaviorBreakdown.searches)} tone="critical" />
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h3">{t('analytics.recommendationTypePerformance')}</Text>
              <Divider />
              <BlockStack gap="400">
                <Box>
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text as="span" fontWeight="semibold">{t('analytics.collaborative')}</Text>
                      <Badge tone="success">
                        {`${((analytics.recommendationPerformance.collaborative.conversions / 
                          analytics.recommendationPerformance.collaborative.impressions) * 100 || 0).toFixed(1)}% ${t('analytics.cvr')}`}
                      </Badge>
                    </InlineStack>
                    <InlineStack gap="400">
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.collaborative.impressions.toLocaleString()} {t('analytics.impressions').toLowerCase()}
                      </Text>
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.collaborative.clicks.toLocaleString()} {t('analytics.clicks').toLowerCase()}
                      </Text>
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.collaborative.conversions.toLocaleString()} {t('analytics.conversions').toLowerCase()}
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </Box>

                <Divider />

                <Box>
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text as="span" fontWeight="semibold">{t('analytics.contentBased')}</Text>
                      <Badge tone="attention">
                        {`${((analytics.recommendationPerformance.contentBased.conversions / 
                          analytics.recommendationPerformance.contentBased.impressions) * 100 || 0).toFixed(1)}% ${t('analytics.cvr')}`}
                      </Badge>
                    </InlineStack>
                    <InlineStack gap="400">
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.contentBased.impressions.toLocaleString()} {t('analytics.impressions').toLowerCase()}
                      </Text>
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.contentBased.clicks.toLocaleString()} {t('analytics.clicks').toLowerCase()}
                      </Text>
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.contentBased.conversions.toLocaleString()} {t('analytics.conversions').toLowerCase()}
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </Box>

                <Divider />

                <Box>
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text as="span" fontWeight="semibold">{t('analytics.trending')}</Text>
                      <Badge>
                        {`${((analytics.recommendationPerformance.trending.conversions / 
                          analytics.recommendationPerformance.trending.impressions) * 100 || 0).toFixed(1)}% ${t('analytics.cvr')}`}
                      </Badge>
                    </InlineStack>
                    <InlineStack gap="400">
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.trending.impressions.toLocaleString()} {t('analytics.impressions').toLowerCase()}
                      </Text>
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.trending.clicks.toLocaleString()} {t('analytics.clicks').toLowerCase()}
                      </Text>
                      <Text variant="bodySm" as="span" tone="subdued">
                        {analytics.recommendationPerformance.trending.conversions.toLocaleString()} {t('analytics.conversions').toLowerCase()}
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </Box>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
